import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Zap, TrendingUp, AlertTriangle, Loader2, Activity, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const TEST_LABELS = {
  SJ: 'Squat Jump', CMJ: 'CMJ', CMJ_RIGHT_LEFT: 'CMJ R/L',
  LOADED_CMJ: 'Loaded CMJ', DROP_JUMP: 'Drop Jump',
  CMJ_FREE_ARMS: 'CMJ Free Arms', DRIFT_2D: 'Drift 2D', STIFFNESS: 'Stiffness',
};

export default function AthleteJumpPage() {
  const { api, user } = useAuth();
  const { language } = useLanguage();
  const t = (en, it) => language === 'IT' ? it : en;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get('/jump/my-results').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [api]);

  const handleExportPdf = async () => {
    if (!user?.athlete_id) return;
    setExporting(true);
    try {
      const res = await api.post(
        '/jump-reports/athlete',
        { athlete_id: user.athlete_id },
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jump_report_${user.athlete_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('Report downloaded', 'Report scaricato'));
    } catch {
      toast.error(t('Export failed', 'Esportazione non riuscita'));
    }
    setExporting(false);
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  if (!data || data.total_tests === 0) {
    return (
      <div className="text-center py-16" data-testid="athlete-jump-empty">
        <Zap className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
        <p className="text-zinc-500">{t('No jump test results yet', 'Nessun risultato di test di salto')}</p>
      </div>
    );
  }

  const { latest, history, total_tests, alerts } = data;

  const chartData = {};
  for (const [tt, items] of Object.entries(history)) {
    chartData[tt] = items.filter(r => r.date).sort((a, b) => a.date.localeCompare(b.date)).map(r => ({
      date: r.date, height: r.jump_height_cm, rsi: r.rsi,
    }));
  }

  return (
    <div className="space-y-6 fade-in" data-testid="athlete-jump-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <Zap className="w-6 h-6 text-cyan-400" />
            {t('Jump Testing', 'Test di Salto')}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{t('Your jump performance results', 'I tuoi risultati di salto')}</p>
        </div>
        <Button
          onClick={handleExportPdf}
          disabled={exporting || !user?.athlete_id || total_tests === 0}
          variant="outline"
          className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
          data-testid="athlete-jump-export-pdf-btn"
        >
          {exporting
            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            : <FileDown className="w-4 h-4 mr-2" />}
          {t('Export PDF', 'Esporta PDF')}
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Activity className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-zinc-100" data-testid="my-total-tests">{total_tests}</div>
            <div className="text-xs text-zinc-500">{t('Total Tests', 'Test Totali')}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-zinc-100" data-testid="my-best">
              {latest.CMJ?.jump_height_cm || latest.SJ?.jump_height_cm || '—'}
            </div>
            <div className="text-xs text-zinc-500">{t('Latest Jump (cm)', 'Ultimo Salto (cm)')}</div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-400 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {t('Notifications', 'Notifiche')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {alerts.map((a, i) => (
              <div key={i} className="text-sm p-2 bg-amber-500/5 rounded-sm text-zinc-300">
                {a.type === 'PERFORMANCE_DROP' && t(`Performance decreased by ${a.drop_pct}%`, `Prestazione calata del ${a.drop_pct}%`)}
                {a.type === 'ASYMMETRY' && t(`Asymmetry detected: ${a.asymmetry_pct}%`, `Asimmetria rilevata: ${a.asymmetry_pct}%`)}
                {a.type === 'RSI_LOW' && t(`RSI below threshold: ${a.rsi}`, `RSI sotto soglia: ${a.rsi}`)}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Latest Results */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-zinc-100 text-base">{t('Latest Results', 'Ultimi Risultati')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(latest).filter(([_, d]) => d).map(([tt, d]) => (
              <div key={tt} className="flex items-center justify-between p-3 rounded-sm border border-zinc-800 bg-zinc-950/50">
                <div>
                  <div className="text-sm font-semibold text-cyan-400">{TEST_LABELS[tt] || tt}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{d.date}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-zinc-100">{d.jump_height_cm} cm</div>
                  {d.rsi != null && <div className="text-xs text-cyan-400">RSI: {d.rsi}</div>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Trend Charts */}
      {Object.entries(chartData).filter(([_, d]) => d.length > 1).map(([tt, cdata]) => (
        <Card key={tt} className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base">{TEST_LABELS[tt] || tt} — {t('Trend', 'Andamento')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={cdata}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 11 }} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ backgroundColor: '#1c1c1c', border: '1px solid #333' }} />
                <Line type="monotone" dataKey="height" stroke="#22d3ee" strokeWidth={2} dot={{ fill: '#22d3ee', r: 3 }} name="cm" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
